import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  OnInit,
  signal,
  viewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { FieldDef } from 'app/core/models';
import { FormPanelMeta, PanelComponentBase } from '../panels.types';
import { FormFieldComponent } from '@shared/components/form-fields/form-field.component';
import { clone } from 'lodash';

@Component({
  selector: 'app-panel-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelFormComponent extends PanelComponentBase<FormPanelMeta> implements OnInit {
  fieldDefs: FieldDef[] = [];
  rowActionDisabled = [];
  title = new FormControl('');

  savingChanges = signal(false);
  pendingChanges = signal({});
  hasPendingChanges = computed(() => Object.keys(this.pendingChanges()).length > 0);
  modelMemento: any;

  fieldsComponents = viewChildren<FormFieldComponent>('fields');

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    this.ui = { ...this.ui, editMode: false };
    this.panelDef.meta.fields = this.panelDef.meta.fields.map((row) => {
      return row.filter((fieldName) => {
        const fieldDef = this.lookupField(fieldName);
        return fieldDef && !fieldDef.archived;
      });
    });
    this.fieldDefs = this.fieldDefs.filter((fieldDef) => !fieldDef.archived);
    this.panelDefMemento = JSON.parse(JSON.stringify(this.panelDef));
    this.modelMemento = clone(this.model);
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  toggleEditMode(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.title.setValue(this.panelDef.title);
    if (this.ui.editMode && this.panelDef.meta.fields.length === 0) {
      this.manageField('first');
    }
    this.editToggled.emit({ toggle: true, panelDef: this.panelDef });
    this.changeDetectorRef.detectChanges();
  }

  cancelEdits(): void {
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.panelDef = JSON.parse(JSON.stringify(this.panelDefMemento));
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
  }

  saveLayout(): void {
    this.panelDef = {
      ...this.panelDef,
      title: this.title.value,
      meta: { ...this.panelDef.meta, fields: this.panelDef.meta.fields.filter((row) => row.length) },
    };
    this.panelDefUpdated.emit(this.panelDef);
    this.panelDefMemento = JSON.parse(JSON.stringify(this.panelDef));
    this.ui = { ...this.ui, editMode: !this.ui.editMode };
    this.editToggled.emit({ toggle: false, panelDef: this.panelDef });
  }

  manageField(
    mode: 'first' | 'addInColumn' | 'addInNewRow' | 'remove',
    data?: { rowIndex: number; colIndex?: number },
  ): void {
    const [firstField] = this.fieldDefs;

    switch (mode) {
      case 'first':
        this.panelDef.meta.fields = [[firstField.name]];
        break;
      case 'addInColumn':
        this.panelDef.meta.fields[data.rowIndex] = [...this.panelDef.meta.fields[data.rowIndex], firstField.name];
        break;
      case 'addInNewRow':
        if (this.panelDef.meta.fields.length === 0) {
          this.panelDef.meta.fields = [[firstField.name]];
        } else {
          this.panelDef.meta.fields.splice(data.rowIndex + 1, 0, [firstField.name]);
        }
        break;
      case 'remove':
        this.panelDef.meta.fields[data.rowIndex] = this.panelDef.meta.fields[data.rowIndex].filter(
          (_, index) => index !== data.colIndex,
        );
        break;
    }
  }

  update(data: any): void {
    this.pendingChanges.set({ ...this.pendingChanges(), ...data });
  }

  completeSaving() {
    this.pendingChanges.set({});
    this.savingChanges.set(false);
  }

  discardChanges() {
    this.model = clone(this.modelMemento);
    this.pendingChanges.set({});
    this.reloadFields();
  }

  reloadFields() {
    this.fieldsComponents().forEach((field) => {
      field.loadComponent();
      this.changeDetectorRef.detectChanges();
    });
  }

  saveChanges() {
    this.savingChanges.set(true);
    this.dataUpdated.emit(this.pendingChanges());
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef) => fieldDef.name == name);
  }
}
